/**
 * Request helpers for My Vendors card/table badge rendering.
 * Mirrors Innovation Lab myVendorRequestsAggregator conventions.
 */

import { replaceRfiTerminologyForDisplay } from "./apiResponseRfiToVqqDisplay";
import { isKnownRfiCatalogServiceName } from "./rfiCatalogServiceNames";

const CANONICAL_GXP_TOKENS = [
  "GAMP", "GCDMP", "GCP", "GDocP", "GDP", "GEP", "GLP", "GMLP", "GMP",
  "GPP", "GSP", "GTP", "GTraP/GTrP", "GVP", "GxP",
];

const CANCELLED_STATUSES = new Set([
  "cancelled",
  "canceled",
  "rejected",
  "declined",
  "withdrawn",
]);

const PENDING_STATUSES = new Set([
  "pending",
  "requested",
  "new",
  "not started",
  "notstarted",
  "awaiting",
]);

const IN_PROGRESS_STATUSES = new Set([
  "in progress",
  "in-progress",
  "inprogress",
  "active",
  "open",
  "under review",
  "review",
  "accepted",
  "in field",
  "scheduled",
  "draft",
]);

const COMPLETE_STATUSES = new Set([
  "complete",
  "completed",
  "released",
  "published",
  "final report delivered",
  "report published to library",
]);

export function isMyRequest(req) {
  const type = req?.type;
  return type === "RFI" || type === "Assessment";
}

export function isLibraryRequest(req) {
  const type = req?.type;
  return type === "LibraryVQQ" || type === "LibraryVQA";
}

export function getRequestTypeFamily(type) {
  if (type === "Assessment" || type === "LibraryVQA") return "VQA";
  return "VQQ";
}

export function stripGxpFromTitle(title, gxps = []) {
  if (!title || typeof title !== "string") return title || "";
  const tokens = (gxps?.length ? gxps : CANONICAL_GXP_TOKENS)
    .filter(Boolean)
    .map((g) => String(g));
  let out = title;
  tokens.forEach((token) => {
    const escaped = token.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&");
    const re = new RegExp(`(^|[\\s\\-–—])(${escaped})(?=[\\s\\-–—]|$)`, "gi");
    out = out.replace(re, "$1");
  });
  return out.replace(/\s+/g, " ").replace(/\s+([,.;:])/g, "$1").trim();
}

/**
 * Diligent (non-custom) VQQs in My Vendors request tabs, matching the prototype
 * "Diligent Foundation - All VQQ" / "Diligent Data Management VQQ" pattern.
 */
export function isDiligentMyVendorVqqTitle(title, type) {
  if (type === "LibraryVQQ") return true;
  if (type !== "RFI") return false;
  const raw = typeof title === "string" ? title.trim() : "";
  if (!raw) return false;
  if (/custom\s+(rfi|vqq)\b/i.test(raw)) return false;
  if (/^diligent\b/i.test(raw)) return true;
  if (/\s(?:RFI|VQQ)$/i.test(raw)) return true;
  return isKnownRfiCatalogServiceName(raw);
}

const CUSTOM_VQQ_PREFIX_RE =
  /^(?:custom\s+(?:rfi|vqq))(?:\s*[-–—:]\s*|\s+)(.*)$/i;

function stripCustomVqqPrefix(title) {
  const match = title.match(CUSTOM_VQQ_PREFIX_RE);
  if (match) return (match[1] || "").trim();
  if (/^custom\s+(rfi|vqq)$/i.test(title)) return "";
  return title;
}

/**
 * Prototype custom row: "Custom VQQ - {name}". Reuses the stored name (or first
 * service) and never double-prefixes.
 */
function formatCustomMyVendorVqqTitle(title, services = []) {
  let name = stripCustomVqqPrefix(title);
  if (!name || /^custom\s+(rfi|vqq)$/i.test(name)) {
    const fromService = (services || [])
      .map((s) => (typeof s === "string" ? s.trim() : ""))
      .find(Boolean);
    name = fromService || "Custom";
  }
  return `Custom VQQ - ${name}`;
}

/**
 * Diligent: trailing "VQQ" (RFI → VQQ). Custom: "Custom VQQ - {name}".
 * Prototype: "Diligent Foundation - All GLP VQQ" / "Custom VQQ - Quality Control".
 */
export function formatMyVendorVqqTitle(title, type, extras = {}) {
  const raw = typeof title === "string" ? title.trim() : "";
  if (type !== "RFI" && type !== "LibraryVQQ") return raw;
  if (!raw && type !== "RFI") return raw;

  if (isDiligentMyVendorVqqTitle(raw, type)) {
    const withVqqTerminology = replaceRfiTerminologyForDisplay(raw);
    if (/\sVQQ$/i.test(withVqqTerminology)) return withVqqTerminology;
    return `${withVqqTerminology} VQQ`;
  }

  if (type === "RFI") {
    return formatCustomMyVendorVqqTitle(raw, extras.services);
  }
  return raw;
}

/** Card/table/export display title: Diligent/custom VQQ label, then GxP stripped. */
export function getMyVendorRequestDisplayTitle(request) {
  const formatted = formatMyVendorVqqTitle(request?.title, request?.type, {
    services: request?.services,
  });
  return stripGxpFromTitle(formatted, resolveRequestGxps(request));
}

/** GxP chips for request rows — API gxps first, else parse from title (prototype parity). */
export function resolveRequestGxps(request) {
  const fromApi = (request?.gxps || []).filter(Boolean);
  if (fromApi.length > 0) return fromApi;

  const title = request?.title;
  if (!title || typeof title !== "string") return [];

  const found = new Set();
  CANONICAL_GXP_TOKENS.forEach((token) => {
    const escaped = token.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&");
    const re = new RegExp(`(^|[\\s\\-–—])(${escaped})(?=[\\s\\-–—]|$)`, "i");
    if (re.test(title)) found.add(token);
  });
  return Array.from(found);
}

export function shortenRequestLocation(raw) {
  if (!raw || typeof raw !== "string") return "";
  const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  if (parts.length >= 3) {
    return `${parts[parts.length - 2]}, ${parts[parts.length - 1]}`;
  }
  return parts.join(", ");
}

function normalizeStatus(status) {
  return String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function resolveRequestStatusKey(request) {
  if (!request) return "in-progress";
  const status = normalizeStatus(request.status);

  if (CANCELLED_STATUSES.has(status)) return "cancelled";
  if (COMPLETE_STATUSES.has(status)) return "complete";
  if (PENDING_STATUSES.has(status)) return "pending";
  if (IN_PROGRESS_STATUSES.has(status)) return "in-progress";
  if (request.isCompleted) return "complete";
  if (!status) return request.isCompleted ? "complete" : "in-progress";
  return "in-progress";
}

export const MY_VENDOR_REQUEST_STATUS_BUCKETS = [
  {
    key: "pending",
    label: "Pending",
    dotClass: "bg-amber-400",
    matches: (item) => resolveRequestStatusKey(item) === "pending",
  },
  {
    key: "in-progress",
    label: "In Progress",
    dotClass: "bg-yellow-400",
    matches: (item) => resolveRequestStatusKey(item) === "in-progress",
  },
  {
    key: "complete",
    label: "Complete",
    dotClass: "bg-green-500",
    matches: (item) => resolveRequestStatusKey(item) === "complete",
  },
  {
    key: "cancelled",
    label: "Cancelled / Rejected",
    dotClass: "bg-gray-400",
    matches: (item) => resolveRequestStatusKey(item) === "cancelled",
  },
];

export const MY_VENDOR_LIBRARY_STATUS_BUCKETS = [
  {
    key: "pending",
    label: "Pending",
    dotClass: "bg-amber-400",
    matches: (item) => resolveRequestStatusKey(item) === "pending",
  },
  {
    key: "in-progress",
    label: "In Progress",
    dotClass: "bg-yellow-400",
    matches: (item) => resolveRequestStatusKey(item) === "in-progress",
  },
  {
    key: "complete",
    label: "Complete",
    dotClass: "bg-green-500",
    matches: (item) => resolveRequestStatusKey(item) === "complete",
  },
  {
    key: "cancelled",
    label: "Cancelled / Rejected",
    dotClass: "bg-gray-400",
    matches: (item) => resolveRequestStatusKey(item) === "cancelled",
  },
];

export function getRequestStatusBucket(req) {
  if (!req) return null;
  const buckets = isLibraryRequest(req)
    ? MY_VENDOR_LIBRARY_STATUS_BUCKETS
    : MY_VENDOR_REQUEST_STATUS_BUCKETS;
  return buckets.find((b) => b.matches(req)) || null;
}

export function formatRequestRowDate(raw) {
  if (!raw || raw === "--") return null;
  try {
    const match = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return null;
  }
}

/** Badge meta line: release date or "Request Date: …" plus short location. */
export function getBadgeMetaParts(request) {
  const dateStr = formatRequestRowDate(request?.date);
  const locationShort = shortenRequestLocation(request?.location);
  if (!dateStr && !locationShort) return null;
  const prefix = !request?.isCompleted && dateStr ? "Request Date: " : "";
  return { dateStr, locationShort, prefix };
}

export function vendorHasAnyRequests(vendor) {
  const myCount = vendor?.myRequests?.length || 0;
  const libraryCount = vendor?.libraryRequests?.length || 0;
  return myCount + libraryCount > 0;
}

/**
 * Resolves how to open a My Vendors request row.
 * Library requests use the document drawer (prototype parity); platform
 * requests navigate to detail routes using hc_questionnaire_id / vqa_request_id.
 */
export function resolveRequestAction(request) {
  if (!request) return null;
  const type = request.type;

  if (isLibraryRequest(request)) {
    if (!request.id) return null;
    return {
      kind: "drawer",
      documentId: String(request.id),
      docType: type === "LibraryVQA" ? "VQA" : "RFI",
    };
  }

  if (type === "RFI") {
    const questionnaireId = request.hcQuestionnaireId;
    if (questionnaireId != null && questionnaireId !== "") {
      return {
        kind: "navigate",
        path: `/RFI/${questionnaireId}/Details`,
      };
    }
  }

  if (type === "Assessment" && request.vqaRequestId) {
    return {
      kind: "navigate",
      path: `/Assessment/${request.vqaRequestId}/Details`,
    };
  }

  return null;
}

/** Navigate to Request VQQ with vendor pre-selected (matches Discovery ActionMenu). */
export function getRequestVqqCreatePath(providerId) {
  const id = String(providerId || "").trim();
  if (!id) return null;
  return `/RFI/Create?id=${encodeURIComponent(id)}`;
}

/** Navigate to Request Assessment with vendor pre-selected (matches Discovery ActionMenu). */
export function getRequestVqaCreatePath(providerId) {
  const id = String(providerId || "").trim();
  if (!id) return null;
  return `/Assessment/Create?id=${encodeURIComponent(id)}`;
}

/**
 * Prototype parity: suppress the Risk Summary row when the vendor has zero
 * scored contributors (Innovation Lab `hasVendorRiskSummary`). With live API
 * data that maps to a populated `vendor.riskSummary` from DocumentService.
 */
export function hasVendorRiskSummary(vendor) {
  const summary = vendor?.riskSummary;
  return (
    summary != null &&
    typeof summary.score === "number" &&
    !Number.isNaN(summary.score)
  );
}
